import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  OnInit,
  OnDestroy,
  Output,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, filter, takeUntil } from 'rxjs';
import {
  SIDEBAR_CONFIG,
  SidebarItem,
} from 'src/app/private/services/module.config';
import { PermissionService } from 'src/app/private/services/permission.service';
import { ThemeService } from 'src/app/private/services/theme.service';
import { SubscriptionBillingLockService } from 'src/app/private/services/subscription-billing-lock.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { HrmEmployeeService } from 'src/app/private/services/hrm-employee.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  private readonly SIDEBAR_COLLAPSED_KEY = 'sidebar_collapsed';
  /** Below this width the menu collapses to icons so a docked inspect panel does not squeeze or blank it. */
  private readonly SIDEBAR_COLLAPSE_BREAKPOINT = 1200;
  private preferCollapsed = false;
  private viewportNarrow = false;

  @Output() collapsedChange = new EventEmitter<boolean>();

  sidebarConfig: SidebarItem[] = SIDEBAR_CONFIG;

  /** When subscription is hard-blocked, only this entry is shown. */
  private readonly billingOnlyNav: SidebarItem[] = [
    {
      key: 'billing_only',
      label: 'Billing & Subscription',
      icon: 'fas fa-file-invoice-dollar',
      module: 'billing_subscription',
      route: '/main/billing-subscription',
    },
  ];
  logoUrl: string = '';
  clientName: string = '';
  clientTagline: string = '';
  readonly footerLogoUrl = 'assets/img/gravitas-ai-icon.png';
  private destroy$ = new Subject<void>();

  mobileSidebarOpen = false;
  sidebarCollapsed = false;
  @ViewChild('collapsedFlyout') collapsedFlyoutRef?: ElementRef<HTMLElement>;

  collapsedFlyoutItem: SidebarItem | null = null;
  collapsedFlyoutTop = 0;
  collapsedFlyoutMaxHeight = 0;
  private collapsedFlyoutAnchor: HTMLElement | null = null;
  private readonly flyoutViewportPadding = 12;
  nestedOpen: { [key: string]: boolean } = {};
  mobileSubmenus: { [key: string]: boolean } = {};
  activeParentKey: string | null = null;

  /**
   * TESTING FLAG (Disable in production)
   */
  bypassAllFeatures = false;

  constructor(
    private router: Router,
    private elementRef: ElementRef,
    private permissionService: PermissionService,
    private themeService: ThemeService,
    private billingLock: SubscriptionBillingLockService,
    private localStorage: LocalStorageService,
    private cdr: ChangeDetectorRef,
    private localStorageService: LocalStorageService,
    private hrmEmployeeService: HrmEmployeeService,
  ) {}

  ngOnInit(): void {
    this.loadSidebarCollapsedState();
    this.viewportNarrow = window.innerWidth < this.SIDEBAR_COLLAPSE_BREAKPOINT;
    if (this.viewportNarrow) {
      this.sidebarCollapsed = true;
    }
    this.collapsedChange.emit(this.sidebarCollapsed);
    this.permissionService.refresh();

    this.themeService.config$
      .pipe(takeUntil(this.destroy$))
      .subscribe((cfg) => {
        this.logoUrl =
          cfg.sidebarLogoUrl || this.localStorage.getClientLogo() || '';
        const themedName = (cfg.appName || '').trim();
        this.clientName =
          (themedName && themedName !== 'CRM' ? themedName : '') ||
          this.localStorage.getClientName() ||
          '';
      });

    this.billingLock.billingOnly$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.cdr.markForCheck();
      });

    // Check employee enrollment for My Desk if HRM is subscribed
    this.syncEmployeeEnrollment();

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.closeCollapsedFlyout();
        this.syncActiveMenu();
      });

    setTimeout(() => this.syncActiveMenu(), 100);
  }

  /* ===============================
     SUBSCRIPTION & ENROLLMENT
  =============================== */

  /** Check if current logged-in user is an Admin / Root / Superadmin */
  private isUserAdmin(): boolean {
    if (this.bypassAllFeatures) return true;
    const role = (
      this.localStorageService.getRole() ||
      localStorage.getItem('role') ||
      ''
    )
      .toLowerCase()
      .trim();
    if (!role) return false;
    return (
      this.localStorageService.checkFullAccessForTableList(role) ||
      role.includes('admin') ||
      role.includes('root') ||
      role.includes('super') ||
      role.includes('hr')
    );
  }

  /** Condition 1: Client must have hrm module added in billing & subscription */
  isHrmSubscribed(): boolean {
    // Admins always have access to HRM
    if (this.isUserAdmin()) return true;

    const raw = localStorage.getItem('feature_modules');
    // If not configured, default to true
    if (
      !raw ||
      raw === 'null' ||
      raw === 'undefined' ||
      raw === '[]' ||
      raw === ''
    ) {
      return true;
    }
    try {
      if (raw.startsWith('[')) {
        const arr = JSON.parse(raw);
        if (!Array.isArray(arr) || arr.length === 0) return true;
        return arr.some((m: any) => {
          const s = String(m).toLowerCase();
          return s.includes('hrm') || s.includes('human_resource');
        });
      }
      return raw.toLowerCase().includes('hrm');
    } catch {
      return true;
    }
  }

  /** Sync employee enrollment status with backend */
  private syncEmployeeEnrollment(): void {
    if (!this.isHrmSubscribed()) return;
    const userId = this.localStorageService.getUserId();
    const clientId = this.localStorageService.getClientId();
    if (!userId) return;

    this.hrmEmployeeService
      .checkEmployeeEnrollment({ user_id: userId, client_id: clientId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res?.is_enrolled && res?.employee) {
            this.localStorageService.setEmployeeEnrollment(
              true,
              res.employee.employee_id,
              res.employee.employee_code,
              res.employee,
            );
          } else {
            this.localStorageService.setEmployeeEnrollment(false);
          }
          this.cdr.markForCheck();
        },
        error: () => {},
      });
  }

  /* ===============================
     ACCESS CHECKS (Using Service)
  =============================== */

  hasFeatureAccess(module: string, feature?: string): boolean {
    if (this.bypassAllFeatures) return true;
    return this.permissionService.hasFeatureAccess(module, feature);
  }

  hasModuleAccess(module: string): boolean {
    if (this.bypassAllFeatures) return true;
    return this.permissionService.hasModuleAccess(module);
  }

  hasChildren(item?: SidebarItem | null): boolean {
    return !!item?.children?.length;
  }

  get navItems(): SidebarItem[] {
    if (this.billingLock.isBillingRouteLockActive()) {
      return this.billingOnlyNav;
    }
    return this.sidebarConfig.filter((item) => this.canShow(item));
  }

  canShow(item: SidebarItem): boolean {
    if (this.billingLock.isBillingRouteLockActive()) {
      return item.key === 'billing_only';
    }
    return this.canShowItem(item);
  }

  canShowItem(item: SidebarItem, inheritedModule?: string): boolean {
    if (this.isCampaignConfigurationItem(item)) {
      return this.canShowCampaignConfiguration(item);
    }
    if (this.bypassAllFeatures) return true;
    if (this.hasChildren(item)) {
      const nextInherited = item.module || inheritedModule;
      return (item.children || []).some((child) =>
        this.canShowItem(child, nextInherited),
      );
    }
    if (!item.module) {
      return inheritedModule
        ? this.permissionService.hasNavAccess(
            inheritedModule,
            item.permissionParents,
            item.allowParentModule,
          )
        : true;
    }
    return this.permissionService.hasNavAccess(
      item.module,
      item.permissionParents,
      item.allowParentModule,
    );
  }

  /** Settings → Campaign Configuration follows the campaign role flag only. */
  private isCampaignConfigurationItem(item: SidebarItem): boolean {
    return item.key === 'admin_campaign_config' || item.key === 'campaign_segment';
  }

  private canShowCampaignConfiguration(item: SidebarItem): boolean {
    if (this.hasChildren(item)) {
      return (item.children || []).some((child) =>
        this.canShowCampaignConfiguration(child),
      );
    }

    return this.permissionService.hasNavAccess(
      item.module || 'campaign',
      ['campaign'],
      false,
    );
  }

  canChildShow(module: any, child: any): boolean {
    if (this.billingLock.isBillingRouteLockActive()) {
      return (
        typeof child === 'object' && child?.module === 'billing_subscription'
      );
    }
    const childItem: SidebarItem =
      child && typeof child === 'object'
        ? child
        : { key: '', label: '', icon: '', module: child };
    return this.canShowItem(childItem);
  }

  get filteredSidebar(): SidebarItem[] {
    return this.sidebarConfig.filter((item) => this.canShow(item));
  }

  get clientInitial(): string {
    const name = (this.clientName || '').trim();
    return name ? name.charAt(0).toUpperCase() : 'C';
  }

  toggleMobileSidebar(): void {
    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  toggleMobileSubmenu(key: string): void {
    this.mobileSubmenus[key] = !this.mobileSubmenus[key];
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    const narrow = window.innerWidth < this.SIDEBAR_COLLAPSE_BREAKPOINT;
    if (narrow !== this.viewportNarrow) {
      this.viewportNarrow = narrow;
      this.sidebarCollapsed = narrow ? true : this.preferCollapsed;
      if (this.sidebarCollapsed) {
        this.activeParentKey = null;
        this.closeCollapsedFlyout();
      } else {
        this.syncActiveMenu();
      }
      this.collapsedChange.emit(this.sidebarCollapsed);
    }

    if (this.sidebarCollapsed && this.collapsedFlyoutItem) {
      this.positionCollapsedFlyout();
    }
  }

  toggleSidebarCollapse(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
    this.preferCollapsed = this.sidebarCollapsed;
    localStorage.setItem(
      this.SIDEBAR_COLLAPSED_KEY,
      JSON.stringify(this.preferCollapsed),
    );

    if (this.sidebarCollapsed) {
      this.activeParentKey = null;
    } else {
      this.closeCollapsedFlyout();
      this.syncActiveMenu();
    }

    this.collapsedChange.emit(this.sidebarCollapsed);
  }

  closeCollapsedFlyout(): void {
    this.collapsedFlyoutItem = null;
    this.collapsedFlyoutAnchor = null;
  }

  isCollapsedFlyoutOpen(key: string): boolean {
    return this.collapsedFlyoutItem?.key === key;
  }

  private loadSidebarCollapsedState(): void {
    try {
      const saved = localStorage.getItem(this.SIDEBAR_COLLAPSED_KEY);
      if (saved !== null) {
        this.preferCollapsed = JSON.parse(saved) === true;
      }
    } catch {
      this.preferCollapsed = false;
    }
    this.sidebarCollapsed = this.preferCollapsed;
  }

  isMobileSubmenuOpen(key: string): boolean {
    return !!this.mobileSubmenus[key];
  }

  toggleParent(key: string, event?: Event): void {
    const item = this.navItems.find((i) => i.key === key);
    if (!item || !this.hasChildren(item)) {
      this.activeParentKey = null;
      this.closeCollapsedFlyout();
      return;
    }

    if (this.sidebarCollapsed) {
      event?.preventDefault();
      event?.stopPropagation();

      if (this.collapsedFlyoutItem?.key === key) {
        this.closeCollapsedFlyout();
        return;
      }

      this.collapsedFlyoutItem = item;
      this.expandActiveNested(item);

      const target = event?.currentTarget as HTMLElement | undefined;
      if (target) {
        this.collapsedFlyoutAnchor = target;
        this.positionCollapsedFlyout();
      }
      return;
    }

    if (this.activeParentKey === key) {
      this.activeParentKey = null;
      return;
    }

    this.activeParentKey = key;
    this.expandActiveNested(item);
  }

  toggleNested(key: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.nestedOpen[key] = !this.nestedOpen[key];

    if (this.sidebarCollapsed && this.collapsedFlyoutItem) {
      this.positionCollapsedFlyout();
    }
  }

  private positionCollapsedFlyout(): void {
    const anchor = this.collapsedFlyoutAnchor;
    if (!anchor) {
      return;
    }

    const padding = this.flyoutViewportPadding;
    const anchorRect = anchor.getBoundingClientRect();
    this.collapsedFlyoutTop = Math.max(padding, anchorRect.top);

    setTimeout(() => {
      const flyout = this.collapsedFlyoutRef?.nativeElement;
      if (!flyout || !this.collapsedFlyoutItem) {
        return;
      }

      const viewportHeight = window.innerHeight;
      const flyoutHeight = flyout.scrollHeight;
      let top = Math.max(padding, anchorRect.top);
      const maxTop = viewportHeight - flyoutHeight - padding;

      if (flyoutHeight + padding * 2 > viewportHeight) {
        top = padding;
      } else if (top > maxTop) {
        top = Math.max(padding, maxTop);
      }

      this.collapsedFlyoutTop = top;
      this.collapsedFlyoutMaxHeight = viewportHeight - top - padding;
      this.cdr.markForCheck();
    });
  }

  isNestedOpen(key: string): boolean {
    return !!this.nestedOpen[key];
  }

  isParentActive(key: string): boolean {
    return this.activeParentKey === key;
  }

  isDescendantActive(item: SidebarItem): boolean {
    if (this.isLeafActive(item)) return true;
    return (item.children || []).some((child) =>
      this.isDescendantActive(child),
    );
  }

  isLeafActive(item: SidebarItem): boolean {
    if (!item.route) return false;
    const currentPath = this.router.url.split('?')[0];
    const itemPath = item.route.split('?')[0];
    if (!currentPath.includes(itemPath)) return false;

    if (!item.queryParams || !Object.keys(item.queryParams).length) {
      return true;
    }

    const tree = this.router.parseUrl(this.router.url);
    return Object.entries(item.queryParams).every(([key, value]) => {
      const current = tree.queryParams[key];
      if (current === undefined || current === null || current === '') {
        return value === 'all' || value === 'subscription';
      }
      return current === value;
    });
  }

  syncActiveMenu(): void {
    for (const item of this.navItems) {
      if (this.hasChildren(item) && this.isDescendantActive(item)) {
        this.activeParentKey = item.key;
        this.expandActiveNested(item);
        return;
      }
    }
  }

  private itemMatchesUrl(item: SidebarItem, url: string): boolean {
    if (item.route && url.includes(item.route.split('?')[0])) {
      return true;
    }
    return (item.children || []).some((child) =>
      this.itemMatchesUrl(child, url),
    );
  }

  private expandActiveNested(item: SidebarItem): void {
    const openKeys = new Set<string>();
    this.collectActivePathKeys(item, openKeys);
    openKeys.forEach((key) => {
      this.nestedOpen[key] = true;
      this.mobileSubmenus[key] = true;
    });
  }

  private collectActivePathKeys(
    item: SidebarItem,
    openKeys: Set<string>,
  ): boolean {
    let childActive = false;
    for (const child of item.children || []) {
      if (
        this.collectActivePathKeys(child, openKeys) ||
        this.isLeafActive(child)
      ) {
        openKeys.add(child.key);
        childActive = true;
      }
    }
    if (childActive) {
      openKeys.add(item.key);
    }
    return childActive || this.isLeafActive(item);
  }

  getSidebarLabelLines(item: SidebarItem): string[] {
    const label = item.shortLabel || item.label;
    if (!label) {
      return [];
    }

    if (label.includes(' & ')) {
      return label
        .split(' & ')
        .map((part) => part.trim())
        .filter(Boolean);
    }

    return [label];
  }

  @HostListener('document:click', ['$event'])
  onClick(event: Event): void {
    const target = event.target as Node;
    const insideSidebar = this.elementRef.nativeElement.contains(target);

    if (this.mobileSidebarOpen && !insideSidebar) {
      this.mobileSidebarOpen = false;
    }

    if (this.collapsedFlyoutItem && !insideSidebar) {
      this.closeCollapsedFlyout();
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.ctrlKey && event.shiftKey && event.key === 'B') {
      event.preventDefault();
      this.toggleBypassMode();
    }
  }

  toggleBypassMode(): void {
    this.bypassAllFeatures = !this.bypassAllFeatures;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
